// CloudFish
// Copyright (c) 2026 SLP Microsystems, Inc.
// All rights reserved.

import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { getDarkThemeEnabled } from './components/Preferences';
import { OracleJetProvider } from './config/OracleJetContext';
import Login from './components/Login';
import Account from './components/Account';
import AuthLanding from './components/AuthLanding';
import SamlCallback from './components/SamlCallback';
import Dashboard from './components/Dashboard';
import EmailVerification from './components/EmailVerification';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import api from './services/api';

const WEBSITE_SIGNUP_URL = process.env.REACT_APP_WEBSITE_SIGNUP_URL || 'https://www.slpmicrosystems.com/signup';

function clearTrialExpiredSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('tok');
  localStorage.removeItem('user');
}

function RedirectToWebsiteSignup() {
  useEffect(() => {
    const isLocal = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(window.location.host);
    const url = isLocal
      ? (process.env.REACT_APP_LOCAL_WEBSITE_URL || 'http://localhost:3001/signup.html')
      : WEBSITE_SIGNUP_URL;
    window.location.href = url;
  }, []);
  return <div className="loading-screen"><div className="loader"></div></div>;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [signupEmailError, setSignupEmailError] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const handleLoginRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.toggle('dark-theme', getDarkThemeEnabled());
  }, []);

  useEffect(() => {
    const handleSamlMessage = (e) => {
      if (e.origin !== window.location.origin || e.data?.type !== 'saml-login') return;
      if (e.data.token && e.data.user && handleLoginRef.current) {
        handleLoginRef.current(e.data.token, e.data.user);
      }
    };
    window.addEventListener('message', handleSamlMessage);
    return () => window.removeEventListener('message', handleSamlMessage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    const urlParams = new URLSearchParams(window.location.search);
    const verificationToken = urlParams.get('token');
    const hash = window.location.hash || '';
    const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
    const authToken = hashParams.get('auth_token');

    const done = () => { if (!cancelled) setLoading(false); };

    if (authToken) {
      localStorage.setItem('token', authToken);
      localStorage.setItem('tok', authToken);
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      api.get('/me')
        .then((res) => {
          if (!cancelled && res.data) {
            if (res.data.plan_expired || res.data.locked_out) {
              clearTrialExpiredSession();
              window.location.replace(`${window.location.origin}/login?trial_expired=1`);
              return;
            }
            handleLogin(authToken, { id: res.data.id, email: res.data.email, plan: res.data.plan });
            toast.success('Welcome to CloudFish!');
          }
        })
        .catch(() => {
          if (!cancelled) toast.error('Session expired. Please log in again.');
        })
        .finally(done);
      return () => { cancelled = true; };
    }

    if (verificationToken) {
      handleEmailVerification(verificationToken).finally(done);
      return () => { cancelled = true; };
    }

    if (!token) {
      done();
      return;
    }

    // Validate token with server before trusting it
    api.get('/me')
      .then((res) => {
        if (!cancelled && res.data) {
          if (res.data.plan_expired || res.data.locked_out) {
            clearTrialExpiredSession();
            window.location.replace(`${window.location.origin}/login?trial_expired=1`);
            return;
          }
          setIsAuthenticated(true);
          setUser({ email: res.data.email, plan: res.data.plan, id: res.data.id });
        }
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem('token');
          localStorage.removeItem('tok');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setUser(null);
        }
      })
      .finally(done);
    return () => { cancelled = true; };
  }, []);

  const handleEmailVerification = async (token) => {
    try {
      const response = await api.post('/verify-email', { token });
      if (response.data.access_token) {
        handleLogin(response.data.access_token, response.data.user);
        toast.success('Email verified successfully! Welcome aboard!');
        // Clear the token from URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Email verification failed');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('tok', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
    toast.success('Login successful!');
  };
  handleLoginRef.current = handleLogin;

  const handleSignup = (email, emailSent, emailError) => {
    // Don't log in immediately - wait for email verification
    setPendingEmail(email);
    setSignupEmailError(emailError || null);
    setShowSignup(false);
    setShowVerification(true);
    if (emailSent) {
      toast.success('Account created! Please check your email to verify your account.');
    } else {
      const msg = emailError
        ? `Verification email not sent: ${emailError}`
        : 'Account created but the verification email could not be sent. Use "Resend verification email" on the next screen or from the login page.';
      toast.warning(msg, { autoClose: 8000 });
    }
  };

  const handleVerificationComplete = (token, userData) => {
    handleLogin(token, userData);
    setShowVerification(false);
    setPendingEmail(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tok');
    localStorage.removeItem('user');
    // Clear packages cache from sessionStorage
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('packages_cache_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('Failed to clear packages cache from sessionStorage:', e);
    }
    setIsAuthenticated(false);
    setUser(null);
    toast.info('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <OracleJetProvider>
    <Router>
      <div className="App">
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <Routes>
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <Login 
                  onLogin={handleLogin} 
                  onSwitchToSignup={() => setShowSignup(false)}
                  signupUrl={process.env.REACT_APP_WEBSITE_SIGNUP_URL || WEBSITE_SIGNUP_URL}
                />
              ) : (
                <Navigate to="/dashboard" />
              )
            } 
          />
          <Route 
            path="/signup" 
            element={
              !isAuthenticated ? (
                <RedirectToWebsiteSignup />
              ) : (
                <Navigate to="/dashboard" />
              )
            } 
          />
          <Route 
            path="/verify-email" 
            element={
              <EmailVerification
                email={pendingEmail}
                emailError={signupEmailError}
                onLogin={handleLogin}
              />
            } 
          />
          <Route
            path="/saml-callback"
            element={
              <SamlCallback onLogin={handleLogin} />
            }
          />
          <Route 
            path="/forgot-password" 
            element={<ForgotPassword />} 
          />
          <Route 
            path="/reset-password" 
            element={<ResetPassword />} 
          />
          <Route 
            path="/account" 
            element={
              isAuthenticated ? (
                <Account user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route 
            path="/auth" 
            element={
              !isAuthenticated ? (
                <AuthLanding />
              ) : (
                <Navigate to="/dashboard" />
              )
            } 
          />
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" />
              ) : (
                showVerification ? (
                  <EmailVerification
                    email={pendingEmail}
                    emailError={signupEmailError}
                    onLogin={handleLogin}
                  />
                ) : showSignup ? (
                  <Navigate to="/signup" />
                ) : (
                  <Navigate to="/auth" />
                )
              )
            } 
          />
        </Routes>
      </div>
    </Router>
    </OracleJetProvider>
  );
}

export default App;

